import type { EmojiProvider } from '@atlaskit/emoji/resource';
import { EmojiResource } from '@atlaskit/emoji/resource';

import { getEmojiProvider as fabricGetEmojiProvider } from '@confluence/fabric-providers/entry-points/EmojiProvider.ts';

import { EMOJI_TITLE_PUBLISHED_FIELD } from './useEmojiStatusFieldName.ts';
import type { EmojiTitleQuery$data } from './emoji-title/__generated__/EmojiTitleQuery.graphql.ts';
import type { EmojiStatusFieldName } from './__types__/EmojiStatusFieldName.ts';
import type { useEmojiEverSetPropertyQuery$data } from './emoji-title/__generated__/useEmojiEverSetPropertyQuery.graphql.ts';

type EmojiPropertyNode = NonNullable<
	NonNullable<
		NonNullable<
			NonNullable<
				NonNullable<NonNullable<EmojiTitleQuery$data['content']>['nodes']>[number]
			>['emojiTitlePublished']
		>['nodes']
	>[number]
>;

type EmojiTitleProperty<TContentNode> = {
	readonly content?: {
		nodes: readonly (TContentNode | null | undefined)[] | null | undefined;
	} | null;
	readonly singleContent?: TContentNode | null;
};

type EmojiTitlePublishedPropertyNode<TEmojiNode = EmojiPropertyNode> = {
	readonly id: string | null | undefined;
	readonly emojiTitlePublished:
		| {
				nodes: readonly (TEmojiNode | null | undefined)[] | null | undefined;
		  }
		| null
		| undefined;
};

export const getVersionNumber = (
	data: EmojiTitleQuery$data,
	emojiFieldName: EmojiStatusFieldName,
): number => data?.content?.nodes?.[0]?.[emojiFieldName]?.nodes?.[0]?.version?.number || 0;

export const getEmojiId = (
	data: EmojiTitleQuery$data,
	emojiFieldName: EmojiStatusFieldName,
): string =>
	(data?.content?.nodes?.[0]?.[emojiFieldName]?.nodes?.[0]?.value || '').replace(/['"]+/g, '');

export const sanitizeEmojiValue = (emojiValue: string | null | undefined): string =>
	(emojiValue || '').replace(/['"]+/g, '');

export type EmojiProperty = {
	propertyId: string;
	value: string;
	versionNumber: number;
};

type EmojiPropertyType = EmojiPropertyNode;

const transformProperty = (
	property: EmojiPropertyType | EmojiNode | null | undefined,
): EmojiProperty | undefined => {
	if (property) {
		return {
			propertyId: property?.id ?? '',
			value: (property?.value || '').replace(/['"]+/g, '').replace(/null/, ''),
			versionNumber: ('version' in property && property.version?.number) || 0,
		};
	}
};

type EmojiNode = NonNullable<
	NonNullable<
		NonNullable<
			NonNullable<NonNullable<useEmojiEverSetPropertyQuery$data['content']>['nodes']>[number]
		>['properties']
	>['nodes']
>[number];

type PropertiesType = NonNullable<
	NonNullable<NonNullable<useEmojiEverSetPropertyQuery$data['content']>['nodes']>[number]
>['properties'];

type ObjectWithEmojiProperties =
	| {
			readonly properties?: PropertiesType;
	  }
	| null
	| undefined;

const findPropertyByKey = (object: ObjectWithEmojiProperties, key: string) =>
	transformProperty(object?.properties?.nodes?.find((node) => node?.key === key));

export const getEmojiPublishedProperty = (
	object: ObjectWithEmojiProperties,
): EmojiProperty | undefined => findPropertyByKey(object, 'emoji-title-published');

export const getEmojiDraftProperty = (
	object: ObjectWithEmojiProperties,
): EmojiProperty | undefined => findPropertyByKey(object, 'emoji-title-draft');

export const getEmojiFromPublishedPageProperties = (page: ObjectWithEmojiProperties): string => {
	const values = page?.properties?.nodes?.filter((node) => node?.key === 'emoji-title-published');
	return (values?.[0]?.value || '').replace(/['"]+/g, '').replace(/null/, '');
};

export const getEmojiFromDraftPageProperties = (page: ObjectWithEmojiProperties): string => {
	const values = page?.properties?.nodes?.filter((node) => node?.key === 'emoji-title-draft');
	return (values?.[0]?.value || '').replace(/['"]+/g, '').replace(/null/, '');
};

export const getPublishedEmojiId = (
	data:
		| EmojiTitleProperty<EmojiTitlePublishedPropertyNode<Pick<EmojiPropertyNode, 'value'>>>
		| undefined
		| null,
): string =>
	(
		(data?.content?.nodes?.[0] || data?.singleContent)?.emojiTitlePublished?.nodes?.[0]?.value || ''
	).replace(/['"]+/g, '');

type EmojiNodesContent =
	| {
			emoji?: {
				nodes?: Array<{
					value?: string | null;
					key?: string | null;
				} | null> | null;
			} | null;
	  }
	| {
			readonly emoji?: {
				readonly nodes?:
					| readonly (
							| {
									readonly value?: string | null;
									readonly id?: string | null;
							  }
							| null
							| undefined
					  )[]
					| null;
			} | null;
	  };

type EmojiDraftNodesContent = {
	emojiDraft?: {
		nodes?: Array<{
			value: string;
			key: string;
		}>;
	};
};

export const getEmojiValueFromContent = (content?: EmojiNodesContent | null): string =>
	(content?.emoji?.nodes?.[0]?.value || '').replace(/['"]+/g, '').replace(/null/, '');

export const getEmojiDraftValueFromContent = (content: EmojiDraftNodesContent): string =>
	(content?.emojiDraft?.nodes?.[0]?.value || '').replace(/['"]+/g, '').replace(/null/, '');

export const getEmojiDraftFromProperties = (content: EmojiNodesContent): string => {
	const values = content?.emoji?.nodes?.filter(
		(node) => node && 'key' in node && node?.key === 'emoji-title-draft',
	);

	return (values?.[0]?.value || '').replace(/['"]+/g, '').replace(/null/, '');
};

export const getEmojiPublishedFromProperties = (content: EmojiNodesContent): string => {
	const values = content?.emoji?.nodes?.filter(
		(node) => node && 'key' in node && node?.key === 'emoji-title-published',
	);

	return (values?.[0]?.value || '').replace(/['"]+/g, '').replace(/null/, '');
};

export const getHasEmoji = (
	data: EmojiTitleQuery$data | ContentEmojiPublishedData | null | undefined,
	emojiFieldName: EmojiStatusFieldName,
): boolean => {
	let nodes;

	if (data && 'content' in data) {
		nodes = data?.content?.nodes?.[0]?.[emojiFieldName]?.nodes?.[0];
	} else {
		nodes = data?.emojiTitlePublished?.nodes?.[0];
	}

	return !!nodes?.value && nodes?.value !== '""' && nodes?.value !== '"null"';
};

export type ContentEmojiPublishedData = {
	readonly emojiTitlePublished:
		| {
				readonly nodes:
					| ReadonlyArray<
							| {
									readonly id: string | null | undefined;
									readonly key: string | null | undefined;
									readonly value: string | null | undefined;
									readonly version:
										| {
												readonly number: number | null | undefined;
										  }
										| null
										| undefined;
							  }
							| null
							| undefined
					  >
					| null
					| undefined;
		  }
		| null
		| undefined;
};

export const doesContentHaveEmojiPublished = (
	data: ContentEmojiPublishedData | null | undefined,
): boolean => {
	return getHasEmoji(data, 'emojiTitlePublished');
};

/** @deprecated -- use `doesContentHaveEmojiPublished` function instead */
export const getHasEmojiPublished = (data: EmojiTitleQuery$data | undefined): boolean =>
	getHasEmoji(data, EMOJI_TITLE_PUBLISHED_FIELD);

export const getEmojiProvider = async (cloudId: string, userId: string): Promise<EmojiProvider> => {
	return await fabricGetEmojiProvider(EmojiResource, {
		cloudId,
		userId,
	});
};

export enum DefaultTitleEmojiValues {
	LIVE_PAGE_DEFAULT = 'live-page-default',
	NONE = 'none',
}

export const getHasLiveDocsDefaultEmoji = (data?: EmojiTitleQuery$data): boolean => {
	if (!data) {
		return false;
	}
	const defaultTitleEmoji = data?.content?.nodes?.[0]?.contentProperties?.latest?.defaultTitleEmoji;

	// This property denotes whether or not we show a default emoji icon, and is initially set to null to avoid backfilling on the BE;
	// therefore, a null value OR a specifically set value implies that we need to show a default icon, while a "none" value implies
	// that we should not show an icon. The default icon should also be overriden by a user-set emoji.
	return (
		defaultTitleEmoji === null || defaultTitleEmoji === DefaultTitleEmojiValues.LIVE_PAGE_DEFAULT
	);
};
